/* @flow */
import * as React from 'react';
import idx from 'idx';
import { graphql } from 'react-apollo';

import SettingsStore from 'stores/SettingsStore';

import Image from 'components/Image';
import ListActions from 'components/ListActions';
import MediaObject from 'components/MediaObject';
import PeopleListConstants from 'components/PeopleList/PeopleListConstants';
import TextWithIcon from 'components/TextWithIcon';

import { findMatchingRecord } from 'utils/arrayHelpers';

import RosterConstants from 'constants/RosterConstants';

import noRoleAvatarImage from 'assets/images/roles/no-role.svg';
import parentAvatarImage from 'assets/images/roles/parent.svg';
import studentAvatarImage from 'assets/images/roles/student.svg';
import teacherAvatarImage from 'assets/images/roles/teacher.svg';
import everyoneAvatarImage from 'assets/images/roles/everyone.svg';

import type {
  ListActionsFilterState,
  ListActionsCheckboxFilterState,
  ListActionsSelectFilterState,
} from '../ListActions/ListActions';

import type { OrgRole } from './queries/__generated__/GetOrganizationMembers';
import type { Props as TextWithIconProps } from 'components/TextWithIcon';
import type { UserRole, EntityType } from './PeopleList';
import type { errorFragment } from 'queries/__generated__/errorFragment';
import type { Props as SelectProps } from 'components/Select';

import './styles/PeopleListFilterUIDataContainer.scss';

import GET_ORG_ROLE_COUNTS_QUERY from './queries/OrganizationRoleCountsQuery.gql';
import GET_CLASS_ROLE_COUNTS_QUERY from './queries/ClassRoleCountsQuery.gql';

function PeopleListRosterLabel(props: $Shape<TextWithIconProps>) {
  return (
    <TextWithIcon {...props} align="left" appendIcon gutter="xxsmall" iconSize="large" inline />
  );
}

export type PeopleListFilterState = {
  role: UserRole,
  source?: ListActionsCheckboxFilterState,
  grade?: ListActionsSelectFilterState,
};

type RoleInfo = {
  avatar: string,
  count: number,
  label: string,
  value: OrgRole,
};

type Props = {
  entityType: EntityType,
  entityUuid: string,
  query: ?string,
  filters: PeopleListFilterState,
  onFiltersChange: (activeFilters: ListActionsFilterState) => void,
  onQueryChange: (query: string) => void,
  onQueryClick: ?() => void,
  roles: RoleInfo[],
  loading: boolean,
  error: errorFragment,
  reloadQuery: boolean,
  refetch: () => void,
};

type RoleListOption = {
  ...RoleInfo,
  display: React.Element,
  value: OrgRole,
};

class PeopleListFilterUIDataContainer extends React.Component<Props> {
  componentDidUpdate(prevProps: Props) {
    const { reloadQuery, refetch } = this.props;

    if (reloadQuery && !prevProps.reloadQuery && refetch) {
      refetch();
    }
  }

  constructRoleListOptions = (roles: RoleInfo[]): RoleListOption[] => {
    return roles.map(item => ({
      ...item,
      display: (
        <MediaObject
          center
          gutter="xsmall"
          inline
          className="PeopleListFilterUIDataContainer-actions-label">
          <Image src={item.avatar} />
          <div>
            {item.label}
            {item.count !== -1 && (
              <span>
                &nbsp;(
                {item.count})
              </span>
            )}
          </div>
        </MediaObject>
      ),
      value: item.value,
    }));
  };

  getSecondaryFilters() {
    const { filters, entityType } = this.props;

    if (entityType === 'group') {
      return null;
    }

    const rosteredLabel = (
      <PeopleListRosterLabel
        icon="link-linked"
        iconColor="shamrock"
        className="PeopleListFilterUIDataContainer-actions-label">
        {RosterConstants.rosteredLabel}
      </PeopleListRosterLabel>
    );

    const notRosteredLabel = (
      <PeopleListRosterLabel
        icon="link-unlinked"
        iconColor="koala"
        className="PeopleListFilterUIDataContainer-actions-label">
        {RosterConstants.notRosteredLabel}
      </PeopleListRosterLabel>
    );

    const sisLinkFilter = {
      inputProps: {
        nonTextLabel: true,
      },
      name: 'source',
      options: new Map([['rostered', rosteredLabel], ['unrostered', notRosteredLabel]]),
      title: 'SIS link',
      type: 'checkbox',
    };

    const grades = SettingsStore.getSetting('grades')
      .filter(item => item.code !== 'NA')
      .map(item => ({
        label: item.name,
        value: item.name,
      }));

    grades.unshift({
      label: 'All grades',
      value: PeopleListConstants.allGradesValue,
    });

    const inputProps: SelectProps = {
      options: grades,
      className: 'PeopleListFilterUIDataContainer-grade',
    };

    const gradeFilter = {
      inputProps,
      defaultValue: PeopleListConstants.allGradesValue,
      name: 'grade',
      title: 'Grade',
      type: 'select',
    };

    const secondaryFilters = [sisLinkFilter];

    if (filters.role === 'student') {
      secondaryFilters.push(gradeFilter);
    }

    return secondaryFilters;
  }

  render() {
    const { onFiltersChange, onQueryChange, onQueryClick, filters, roles } = this.props;

    const roleListOptions = this.constructRoleListOptions(roles);

    const selectedOption = roleListOptions.find(item => item.value === filters['role']);

    return (
      <ListActions
        data-test="list-actions"
        filterButtonFace={selectedOption && selectedOption.display}
        initialActiveFilters={((filters: any): ListActionsFilterState)}
        primaryFilters={[
          {
            inputProps: {
              nonTextLabel: true,
            },
            name: 'role',
            options: roleListOptions,
            title: 'Role',
            type: 'radio',
          },
        ]}
        secondaryFilters={this.getSecondaryFilters()}
        searchPlaceholder="Search all people in your school"
        onFilterChange={onFiltersChange}
        onQueryChange={onQueryChange}
        onQueryClick={onQueryClick}
      />
    );
  }
}

const formatRoleCounts = (roleCounts, isGroup) => {
  const roles = [
    {
      avatar: studentAvatarImage,
      count: -1,
      label: 'Students',
      value: 'student',
    },
    {
      avatar: parentAvatarImage,
      count: -1,
      label: 'Parents',
      value: 'parent',
    },
    {
      avatar: teacherAvatarImage,
      count: -1,
      label: 'Teachers',
      value: 'teacher',
    },
    {
      avatar: noRoleAvatarImage,
      count: -1,
      label: 'No role',
      value: 'unknown',
    },
  ];

  if (isGroup) {
    roles.unshift({
      avatar: everyoneAvatarImage,
      count: -1,
      label: 'Everyone',
      value: 'everyone',
    });
  }

  if (!roleCounts) {
    return roles;
  }

  return roles.map(role => {
    const matchingRole = roleCounts.find(item => item.role === role.value);

    if (!matchingRole) {
      return role;
    }

    return { ...role, count: matchingRole.count };
  });
};

const withOrgRoleCounts = graphql(GET_ORG_ROLE_COUNTS_QUERY, {
  options: ({ entityUuid }) => {
    return {
      variables: {
        orgUuid: entityUuid,
      },
      fetchPolicy: 'network-only',
    };
  },
  props: ({ data, ownProps: { entityUuid } }) => {
    return {
      roles: formatRoleCounts(
        idx(findMatchingRecord(data.organizations, entityUuid), _ => _.roleCounts),
        false
      ),
      loading: data.loading,
      error: data.error,
      refetch: data.refetch,
    };
  },
});

const withClassRoleCounts = graphql(GET_CLASS_ROLE_COUNTS_QUERY, {
  options: ({ entityUuid }) => {
    return {
      variables: {
        classUuid: entityUuid,
      },
      fetchPolicy: 'network-only',
    };
  },
  props: ({ data, ownProps: { entityUuid } }) => {
    return {
      roles: formatRoleCounts(
        idx(findMatchingRecord(data.classes, entityUuid), _ => _.roleCounts),
        true
      ),
      loading: data.loading,
      error: data.error,
      refetch: data.refetch,
    };
  },
});

export { PeopleListFilterUIDataContainer };

export const PeopleListFilterUIDataContainerInOrg = withOrgRoleCounts(
  PeopleListFilterUIDataContainer
);
export const PeopleListFilterUIDataContainerInGroup = withClassRoleCounts(
  PeopleListFilterUIDataContainer
);
